import React, { useState, useRef } from 'react';
import { Upload, FileText, Download, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import api from '../services/api.ts';

interface ImportResult {
  success: boolean;
  total_processed: number;
  contacts_created: number;
  contacts_updated: number;
  patients_created: number;
  errors: string[];
}

interface ContactsImportProps {
  onImportComplete?: (result: ImportResult) => void;
  onClose?: () => void;
}

const ContactsImport: React.FC<ContactsImportProps> = ({ onImportComplete, onClose }) => {
  const { isDark } = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(csv|xls|xlsx)$/i)) {
      setError('Formato de arquivo não suportado. Use CSV, XLS ou XLSX.');
      return;
    }

    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo 10MB.');
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      setError(null);
      setUploadResult(null);

      const clinicId = localStorage.getItem('clinic_id');
      if (!clinicId) {
        setError('ID da clínica não encontrado');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('clinic_id', clinicId);

      const response = await api.post<ImportResult>('/webhook/contacts/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      if (onImportComplete) {
        onImportComplete(response.data);
      }

      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao importar arquivo');
      console.error('Erro no upload:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Template CSV com coluna 'tipo' e 'tags'
    const csvContent = "nome,telefone,email,tipo,tags,observacoes\nJoão Silva,5521999887766,joao@email.com,paciente,vip,Paciente existente da clínica\nMaria Santos,5521888776655,maria@email.com,contato,novo;indicação,Lead potencial\nPedro Costa,5521777665544,pedro@email.com,contato,,Novo contato";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_contatos.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`rounded-2xl shadow-xl border mb-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
      }`}>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'
              }`}>Importar Contatos</h3>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'
              }`}>Faça upload de uma planilha CSV ou Excel com os contatos</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadTemplate}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${isDark
                  ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
            >
              <Download className="h-4 w-4" />
              Template
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className={`p-2 rounded-xl transition-colors ${isDark
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${isDark
            ? 'border-gray-600 hover:border-sminy-primary/50'
            : 'border-slate-300 hover:border-sminy-primary/50'
          }`}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />

          <div className="flex flex-col items-center">
            <Upload className={`w-12 h-12 mb-4 ${isDark ? 'text-gray-500' : 'text-slate-400'
              }`} />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sminy-primary text-white hover:bg-sminy-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="h-4 w-4" />
              {isUploading ? 'Importando...' : 'Selecionar Arquivo'}
            </button>
            <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-slate-500'
              }`}>
              Formatos: CSV, XLS, XLSX (máx. 10MB)
            </p>
          </div>
        </div>

        {error && (
          <div className={`mt-4 p-4 rounded-2xl border ${isDark
              ? 'bg-red-900/20 border-red-700/50 text-red-400'
              : 'bg-red-50 border-red-200 text-red-700'
            }`}>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {uploadResult && (
          <div className={`mt-4 p-4 rounded-2xl border ${isDark
              ? 'bg-green-900/20 border-green-700/50'
              : 'bg-green-50 border-green-200'
            }`}>
            <h4 className={`font-medium mb-2 ${isDark ? 'text-green-300' : 'text-green-800'
              }`}>Importação Concluída!</h4>
            <div className={`text-sm ${isDark ? 'text-green-400' : 'text-green-700'
              }`}>
              <p>• {uploadResult.total_processed} linhas processadas</p>
              <p>• {uploadResult.contacts_created} novos contatos criados</p>
              <p>• {uploadResult.contacts_updated} contatos atualizados</p>
              <p>• {uploadResult.patients_created} pacientes criados automaticamente</p>
              {uploadResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Erros encontrados:</p>
                  <ul className="list-disc list-inside">
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={`mt-4 p-4 rounded-2xl border ${isDark
            ? 'bg-sminy-primary/10 border-sminy-primary/20'
            : 'bg-sminy-primary/5 border-sminy-primary/20'
          }`}>
          <p className={`text-sm font-medium mb-2 ${isDark ? 'text-sminy-primary/90' : 'text-sminy-primary'
            }`}>Formato esperado:</p>
          <div className={`text-xs space-y-1 ${isDark ? 'text-sminy-primary/80' : 'text-sminy-primary/90'
            }`}>
            <p>• <strong>nome:</strong> Nome completo do contato</p>
            <p>• <strong>telefone:</strong> Telefone com DDD (ex: 5521999887766)</p>
            <p>• <strong>email:</strong> Email (opcional)</p>
            <p>• <strong>tipo:</strong> "contato" ou "paciente" (opcional)</p>
            <p>• <strong>tags:</strong> Tags separadas por vírgula (opcional, ex: vip,novo)</p>
            <p>• <strong>observacoes:</strong> Observações (opcional)</p>
            <p className="mt-2 font-medium">Se tipo = "paciente", será automaticamente criado como paciente!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsImport;